import type { OnboardingApplicationHydrated } from '../../../lib/flotiq-client/types/api-response';

import { use, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { getTestProps } from '../../../lib/helpers';

import Loader from '../../../components/Loader/Loader';

import { useOnboardingApplications } from '../../../hooks/api';

import { ModalInstanceContext } from '../../../contexts/ModalContext';

import { FlotiqLogo } from '../../../images/shapes';

import PopulateSpaceAppCard from './PopulateSpaceAppCard';

interface PopulateSpaceModalContentProps {
  userName: string;
  onApplicationSelect: (application: OnboardingApplicationHydrated) => void;
  testId?: string;
}

const PopulateSpaceModalContent = ({
  userName,
  onApplicationSelect,
  testId,
}: PopulateSpaceModalContentProps) => {
  const { t } = useTranslation();
  const modalInstance = use(ModalInstanceContext);

  const {
    data: onboardingApplications,
    isLoading: onboardingApplicationsLoading,
    errors: onboardingApplicationsErrors,
  } = useOnboardingApplications();

  useEffect(() => {
    if (!onboardingApplicationsLoading && !onboardingApplications?.length) {
      modalInstance?.resolve?.(false);
      console.error(
        'No onboarding applications found, cannot populate space',
        onboardingApplicationsErrors,
      );
    }
  }, [
    modalInstance,
    onboardingApplications?.length,
    onboardingApplicationsErrors,
    onboardingApplicationsLoading,
  ]);

  return (
    <div
      className="relative flex flex-col items-center gap-4 min-h-full md:justify-center pt-12 pb-6 lg:pb-10"
      {...getTestProps(testId, 'introduction')}
    >
      <div className="absolute top-0 left-0">
        <FlotiqLogo className="h-8" />
      </div>
      <div className="w-full max-w-6xl text-center">
        <h1 className="font-bold text-3xl md:text-4xl mb-4 md:mb-8">
          {t('PopulateSpaceModal.ModalContent.Hi', { name: userName })}
        </h1>
        <p>{t('PopulateSpaceModal.ModalContent.Paragraph')}</p>

        <div className="my-8 md:my-14 w-full flex flex-wrap justify-center gap-4 md:gap-10">
          {onboardingApplicationsLoading && <Loader type="spinner-grid" />}
          {onboardingApplications?.map((application) => (
            <div
              key={application.id}
              className="w-full xs:w-[calc(50%-0.5rem)] sm:w-[calc(33.333%-1.75rem)]"
            >
              <PopulateSpaceAppCard
                application={application}
                onApplicationSelect={onApplicationSelect}
                {...getTestProps(testId, `${application.id}-card`, 'testId')}
              />
            </div>
          ))}
        </div>

        <h2 className="whitespace-pre-line font-bold text-base">
          {t('PopulateSpaceModal.ModalContent.CleanHeading')}
        </h2>
        <p>{t('PopulateSpaceModal.ModalContent.CleanDescription')}</p>

        <button
          className="mt-6 md:mt-10 cursor-pointer text-blue hover:text-blue-600 underline font-medium"
          onClick={() => modalInstance?.resolve?.(false)}
          {...getTestProps(testId, 'start-with-empty-space')}
        >
          {t('PopulateSpaceModal.ModalContent.CleanButton')}
        </button>
      </div>
    </div>
  );
};

export default PopulateSpaceModalContent;
